import {
  ActionViewModel,
  ActionViewModelImplementation,
  durationInSecondsOrZero,
  Effector,
  secondsToDuration,
  uuidOrNewUuid,
} from "./ActionViewModel";
import { HitchType } from "protos/atk/enums";
import { laserWeederEffectorsWithType, StartDelayInput } from "./utils";
import {
  LaserWeederEstopOff,
  LaserWeederEstopOn,
  LaserweederOn,
  QuickAction,
} from "protos/atk/activities";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TFunction } from "i18next";
import EngageLaserWeederIcon from "common/images/atk-actions/engage-laserweeder.svg?react";
import EStopOffIcon from "common/images/atk-actions/estop-off.svg?react";
import EStopOnIcon from "common/images/atk-actions/estop-on.svg?react";
import React from "react";

class EngageLaserWeederViewModel extends ActionViewModel<EngageLaserWeederViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: LaserweederOn
  ) {
    super(effector, true, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      laserweederOn: this.actionData,
      carryOverIntoWorkTask: true,
    };
  }

  override actionCardTitle(
    t: TFunction,
    _rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.laserweederEngage"
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.laserweederEngage"
    );
  }

  override components = engageLaserWeeder.components;
}
export const engageLaserWeeder: ActionViewModelImplementation<EngageLaserWeederViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.laserweederEngage",
    actionMenuSection: "holding-action",
    isNewActionAvailableForEffector: laserWeederEffectorsWithType(
      "laserweeder",
      undefined
    ),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "laserweeder") {
        throw new Error(
          'engageLaserWeeder.newAction: effector type is not "laserweeder"'
        );
      }

      return new EngageLaserWeederViewModel(
        effector,
        startInStageIndex,
        0,
        undefined,
        {
          implementId: effector.implementId,
        }
      );
    },
    fromQuickAction: (quickAction, startInStageIndex) => {
      if (quickAction.laserweederOn) {
        return new EngageLaserWeederViewModel(
          {
            type: "laserweeder",
            implementId: quickAction.laserweederOn.implementId,
          },
          startInStageIndex,
          durationInSecondsOrZero(quickAction.startDelay),
          quickAction.actionId,
          quickAction.laserweederOn
        );
      }
    },
    components: {
      Icon: EngageLaserWeederIcon,
      EditForm: ({ basePath }) => {
        return (
          <div className="grid grid-cols-2 gap-2">
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };

class LaserWeederEstopOnViewModel extends ActionViewModel<LaserWeederEstopOnViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: LaserWeederEstopOn
  ) {
    super(effector, true, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      laserweederEstopOn: this.actionData,
      startDelay: secondsToDuration(this.startDelaySec),
      carryOverIntoWorkTask: true,
    };
  }

  override actionCardTitle(
    t: TFunction,
    _rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex >= this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.laserweederEstopOn"
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.estopOn"
    );
  }

  override components = laserweederEstopOn.components;
}
export const laserweederEstopOn: ActionViewModelImplementation<LaserWeederEstopOnViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.estopOn",
    actionMenuSection: "holding-action",
    isNewActionAvailableForEffector: laserWeederEffectorsWithType(
      "lw-estop",
      HitchType.HITCH_TYPE_DRAW_BAR_REAR
    ),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "lw-estop") {
        throw new Error(
          'laserweederEstopOn.newAction: effector type is not "lw-estop"'
        );
      }

      return new LaserWeederEstopOnViewModel(
        effector,
        startInStageIndex,
        0,
        undefined,
        {
          implementId: effector.implementId,
        }
      );
    },
    fromQuickAction: (quickAction, startInStageIndex) => {
      if (quickAction.laserweederEstopOn) {
        return new LaserWeederEstopOnViewModel(
          {
            type: "lw-estop",
            implementId: quickAction.laserweederEstopOn.implementId,
          },
          startInStageIndex,
          durationInSecondsOrZero(quickAction.startDelay),
          quickAction.actionId,
          quickAction.laserweederEstopOn
        );
      }
    },
    components: {
      Icon: EStopOnIcon,
      EditForm: ({ basePath }) => {
        return (
          <div className="grid grid-cols-2 gap-2">
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };

class LaserWeederEstopOffViewModel extends ActionViewModel<LaserWeederEstopOffViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: LaserWeederEstopOff
  ) {
    super(effector, true, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      laserweederEstopOff: this.actionData,
      startDelay: secondsToDuration(this.startDelaySec),
      carryOverIntoWorkTask: true,
    };
  }

  override actionCardTitle(
    t: TFunction,
    _rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex >= this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.laserweederEstopOff"
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.estopOff"
    );
  }

  override components = laserweederEstopOff.components;
}
export const laserweederEstopOff: ActionViewModelImplementation<LaserWeederEstopOffViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.estopOff",
    actionMenuSection: "holding-action",
    isNewActionAvailableForEffector: laserWeederEffectorsWithType(
      "lw-estop",
      HitchType.HITCH_TYPE_DRAW_BAR_REAR
    ),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "lw-estop") {
        throw new Error(
          'laserweederEstopOn.newAction: effector type is not "lw-estop"'
        );
      }

      return new LaserWeederEstopOffViewModel(
        effector,
        startInStageIndex,
        0,
        undefined,
        {
          implementId: effector.implementId,
        }
      );
    },
    fromQuickAction: (quickAction, startInStageIndex) => {
      if (quickAction.laserweederEstopOff) {
        return new LaserWeederEstopOffViewModel(
          {
            type: "lw-estop",
            implementId: quickAction.laserweederEstopOff.implementId,
          },
          startInStageIndex,
          durationInSecondsOrZero(quickAction.startDelay),
          quickAction.actionId,
          quickAction.laserweederEstopOff
        );
      }
    },
    components: {
      Icon: EStopOffIcon,
      EditForm: ({ basePath }) => {
        return (
          <div className="grid grid-cols-2 gap-2">
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };
